import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { api } from "./client";
import { ep } from "./endpoints";
import type {
  AppAccessMember,
  AppAdmitInviteCreated,
  Artist,
  Artwork,
  ArtworkRow,
  ArtworkType,
  ArtworkWrite,
  Attachment,
  AttachmentWrite,
  CatalogInviteCreated,
  CatalogMember,
  CatalogProfileWrite,
  CatalogPublicProfile,
  Facets,
  Me,
  Page,
  Subtype,
  Term,
  VocabKind,
} from "./types";
import { filtersToQuery, type Filters } from "../features/filters/filterState";
import { forget as forgetMedia } from "../features/media/mediaCache";

/**
 * How stale a record is allowed to look, and nothing more.
 *
 * These used to be set by the media signature: urls lapsed after five minutes,
 * so every screen holding one had to refetch before then. They live an hour now
 * (`DOWNLOAD_URL_TTL_SECONDS`), and the bytes behind them are cached on the
 * device by attachment id rather than by url (`features/media/mediaCache.ts`),
 * so what is left to decide is only how quickly an edit made somewhere else
 * should show up here.
 *
 * The one rule that still binds: `gcTime` in App.tsx must stay well under the
 * url lifetime, or a record resurrected from the query cache renders urls whose
 * tokens are already dead.
 */
const CATALOG_STALE = 5 * 60_000;
/** Controlled lists only change when this user adds an option, and that invalidates. */
const LIST_STALE = 30 * 60_000;

export const keys = {
  me: ["me"] as const,
  taxonomy: ["taxonomy"] as const,
  vocabulary: (kind: VocabKind) => ["vocabulary", kind] as const,
  facets: ["facets"] as const,
  artworks: (filters: Filters) => ["artworks", filtersToQuery(filters)] as const,
  artwork: (id: string) => ["artwork", id] as const,
  artists: (q: string) => ["artists", q] as const,
  catalogProfile: ["catalog", "profile"] as const,
  catalogMembers: ["catalog", "members"] as const,
};

/* -------------------------------------------------------------- identity -- */

/**
 * The handshake. It provisions the tenant and copies ~130 seed rows, so nothing
 * else in the app works until it has succeeded once.
 */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<Me>(ep.me),
    staleTime: Infinity,
    retry: (count, error) =>
      // A rejected initData will never become valid by retrying.
      count < 2 && !("status" in error && [401, 403].includes(Number(error.status))),
  });
}

/* ----------------------------------------------------- controlled lists -- */

export function useTaxonomy() {
  return useQuery({
    queryKey: keys.taxonomy,
    queryFn: async () => {
      const data = await api.get<{ types: ArtworkType[] }>(ep.taxonomy);
      return data.types;
    },
    staleTime: LIST_STALE,
  });
}

export function useVocabulary(kind: VocabKind) {
  return useQuery({
    queryKey: keys.vocabulary(kind),
    // Unpaginated: this endpoint returns a bare array.
    queryFn: () => api.get<Term[]>(ep.vocabulary, { kind }),
    staleTime: LIST_STALE,
    select: (terms) => terms.filter((term) => term.is_active !== false),
  });
}

export function useFacets() {
  return useQuery({
    queryKey: keys.facets,
    queryFn: () => api.get<Facets>(ep.facets),
    staleTime: 60_000,
  });
}

/* -------------------------------------------------------------- artworks -- */

/** Reads the opaque cursor back out of the `next` URL DRF hands us. */
function cursorOf(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url, window.location.origin).searchParams.get("cursor");
  } catch {
    return null;
  }
}

export function useArtworks(filters: Filters) {
  return useInfiniteQuery({
    queryKey: keys.artworks(filters),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<ArtworkRow>>(
        ep.artworks,
        { ...filtersToQuery(filters), cursor: pageParam ?? undefined },
        signal,
      ),
    getNextPageParam: (last) => cursorOf(last.next),
    staleTime: CATALOG_STALE,
  });
}

export function useArtwork(id: string | undefined) {
  return useQuery({
    queryKey: keys.artwork(id ?? ""),
    queryFn: () => api.get<Artwork>(ep.artwork(id!)),
    enabled: Boolean(id),
    staleTime: CATALOG_STALE,
    refetchOnWindowFocus: true,
  });
}

/** Everything a write touches: the list, the counts, and the record itself. */
function invalidateCatalog(client: QueryClient, id?: string) {
  client.invalidateQueries({ queryKey: ["artworks"] });
  client.invalidateQueries({ queryKey: keys.facets });
  if (id) client.invalidateQueries({ queryKey: keys.artwork(id) });
}

export function useCreateArtwork() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: ArtworkWrite) => api.post<Artwork>(ep.artworks, body),
    onSuccess: (artwork) => {
      client.setQueryData(keys.artwork(artwork.id), artwork);
      invalidateCatalog(client);
    },
  });
}

export function useUpdateArtwork(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: ArtworkWrite) => api.patch<Artwork>(ep.artwork(id), body),
    onSuccess: (artwork) => {
      client.setQueryData(keys.artwork(id), artwork);
      invalidateCatalog(client, id);
    },
  });
}

/** Soft delete on the server, so the Undo in the toast is a real restore. */
export function useDeleteArtwork() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(ep.artwork(id)),
    onSuccess: (_data, id) => {
      // Drop the record rather than invalidating it: a soft-deleted artwork
      // 404s, and refetching it would only log an error on the way out.
      client.removeQueries({ queryKey: keys.artwork(id) });
      invalidateCatalog(client);
    },
  });
}

export function useRestoreArtwork() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Artwork>(ep.restoreArtwork(id)),
    onSuccess: (artwork) => {
      client.setQueryData(keys.artwork(artwork.id), artwork);
      invalidateCatalog(client);
    },
  });
}

/* ------------------------------------------------------------ public tag -- */

export function useSetPublic() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      api.post<Artwork>(ep.setPublic(id), { is_public: value }),
    onSuccess: (artwork) => client.setQueryData(keys.artwork(artwork.id), artwork),
  });
}

export function useRotateCode() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Artwork>(ep.rotateCode(id)),
    onSuccess: (artwork) => client.setQueryData(keys.artwork(artwork.id), artwork),
  });
}

export function useSendQr() {
  return useMutation({
    mutationFn: (id: string) => api.post<void>(ep.sendQr(id)),
  });
}

/* ------------------------------------------------------ catalog profile -- */

export function useCatalogProfile() {
  return useQuery({
    queryKey: keys.catalogProfile,
    queryFn: () => api.get<CatalogPublicProfile>(ep.catalogProfile),
    staleTime: CATALOG_STALE,
  });
}

export function useUpdateCatalogProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CatalogProfileWrite) => api.patch<CatalogPublicProfile>(ep.catalogProfile, body),
    onSuccess: (profile) => {
      client.setQueryData(keys.catalogProfile, profile);
      client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useCatalogMembers() {
  return useQuery({
    queryKey: keys.catalogMembers,
    queryFn: () => api.get<CatalogMember[]>(ep.catalogMembers),
    staleTime: CATALOG_STALE,
  });
}

export function useRemoveCatalogMember() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (telegramId: number) => api.delete(ep.catalogMember(telegramId)),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.catalogMembers }),
  });
}

export function useAddCatalogMember() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (identity: { telegram_id?: number; username?: string }) =>
      api.post<CatalogMember>(ep.catalogMembers, identity),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.catalogMembers }),
  });
}

export function useCreateCatalogInvite() {
  return useMutation({
    mutationFn: () => api.post<CatalogInviteCreated>(ep.catalogInvites),
  });
}

export function useAccessMembers(enabled = true) {
  return useQuery({
    queryKey: ["access", "members"] as const,
    queryFn: () => api.get<AppAccessMember[]>(ep.accessMembers),
    enabled,
    staleTime: CATALOG_STALE,
  });
}

export function useAddAccessMember() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { username?: string; telegram_id?: number; role: "admin" | "user" }) =>
      api.post<AppAccessMember>(ep.accessMembers, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ["access", "members"] }),
  });
}

export function useRemoveAccessMember() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (telegramId: number) => api.delete(ep.accessMember(telegramId)),
    onSuccess: () => client.invalidateQueries({ queryKey: ["access", "members"] }),
  });
}

export function useCreateAccessInvite() {
  return useMutation({
    mutationFn: (kind: "admin" | "admit") =>
      api.post<AppAdmitInviteCreated>(ep.accessInvites, { kind }),
  });
}

/* --------------------------------------------------------------- artists -- */

export function useArtists(q: string) {
  return useQuery({
    queryKey: keys.artists(q),
    queryFn: async ({ signal }) => {
      const page = await api.get<Page<Artist>>(
        ep.artists,
        { q: q || undefined, page_size: 100 },
        signal,
      );
      return page.results;
    },
    staleTime: 60_000,
  });
}

/** Create-or-select: 200 when it already existed, 201 when created. Same body. */
export function useCreateArtist() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<Artist>(ep.artists, { name }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["artists"] }),
  });
}

/* ---------------------------------------------------------- add option -- */

export function useAddTerm(kind: VocabKind) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (label: string) =>
      api.post<Term>(ep.vocabulary, { kind, label_en: label, label_fa: label }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.vocabulary(kind) }),
  });
}

export function useAddType() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (label: string) =>
      api.post<ArtworkType>(ep.types, { label_en: label, label_fa: label }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.taxonomy }),
  });
}

/** Subtypes must go through the parent route; the flat one cannot set `type`. */
export function useAddSubtype(typeId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (label: string) =>
      api.post<Subtype>(ep.subtypesOfType(typeId!), {
        label_en: label,
        label_fa: label,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.taxonomy }),
  });
}

/* ----------------------------------------------------------- attachments -- */

/**
 * One hero per artwork. The form uploads media as `gallery` and settles the
 * thumbnail with `make-primary`, so this eviction only fires on the one path
 * that still confirms a `primary` directly — which does replace what was there.
 */
function mergeAttachment(current: Attachment[], next: Attachment): Attachment[] {
  const kept = current.filter(
    (file) => file.id !== next.id && !(next.kind === "primary" && file.kind === "primary"),
  );
  return [...kept, next];
}

/**
 * Where a finished upload lands.
 *
 * `uploadAttachment` talks to the API directly rather than through a mutation, so
 * nothing here invalidates on its own — and the invalidation the artwork mutation
 * fired happened before a single byte moved. Without this the detail record stays
 * cached, attachment-free and *fresh*, and the hero the user just picked does not
 * show up until a window-focus refetch or `CATALOG_STALE` elapses.
 *
 * `absorb` takes the confirm response, which is already the full read shape, so
 * the screen paints without paying a round trip to learn what we were just told.
 * `settle` runs once the batch is done and reconciles the list and the counts.
 */
export function useUploadedMedia() {
  const client = useQueryClient();
  return {
    absorb: (artworkId: string, attachment: Attachment) => {
      // An in-flight detail refetch from create/update must not clobber the
      // attachment we just confirmed.
      void client.cancelQueries({ queryKey: keys.artwork(artworkId) });
      client.setQueryData<Artwork>(keys.artwork(artworkId), (old) =>
        old ? { ...old, attachments: mergeAttachment(old.attachments, attachment) } : old,
      );
    },
    /** Invalidate and wait so the next screen does not paint with pre-upload kinds. */
    settle: async (artworkId: string) => {
      invalidateCatalog(client, artworkId);
      await client.refetchQueries({ queryKey: keys.artwork(artworkId) });
    },
  };
}

export function useUpdateAttachment(artworkId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & AttachmentWrite) =>
      api.patch<Attachment>(ep.attachment(id), patch),
    onSuccess: () => invalidateCatalog(client, artworkId),
  });
}

export function useDeleteAttachment(artworkId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(ep.attachment(id)),
    onSuccess: (_data, id) => {
      void forgetMedia(id);
      invalidateCatalog(client, artworkId);
    },
  });
}

export function useMakePrimary(artworkId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Attachment>(ep.makePrimary(id)),
    onSuccess: (promoted) => {
      // Apply kinds immediately. invalidate/refetch alone leaves a window where
      // the list already shows the new thumbnail (primary_media) while detail
      // still has the previous file marked primary — and the hero keeps painting
      // that old attachment from Cache Storage even after a cold start.
      client.setQueryData<Artwork>(keys.artwork(artworkId), (old) => {
        if (!old) return old;
        let saw = false;
        const attachments = old.attachments.map((file) => {
          if (file.id === promoted.id) {
            saw = true;
            return { ...file, ...promoted };
          }
          if (file.kind === "primary") return { ...file, kind: "gallery" as const };
          return file;
        });
        if (!saw) attachments.push(promoted);
        return { ...old, attachments, primary_media: promoted };
      });
      invalidateCatalog(client, artworkId);
    },
  });
}
